
import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { createSignedStorageThumbnailUrl } from "@/lib/storageImageUrls";
import type {
  Issue,
  IssueType,
  IssueSeverity,
  IssueStatus,
  IssueSource,
  IssueBlockerCategory,
  IssueAffectedArea,
} from "@/data/types";
import { toUiIssue, toDbIssueCreate, toDbIssueUpdate } from "@/mappers/issue.mapper";
import { ActionQueueService } from "@/services/action-queue.service";

// ── DB row shape — matches the actual Supabase issues table ───────────────
export interface IssueRow {
  id: string;                      // uuid
  organization_id: string | null;  // nullable FK — no multi-org support yet
  job_id: string | null;           // uuid FK to jobs.id
  issue_number: string | null;     // text — our business key (ISS-001, etc.)
  title: string | null;            // maps to issueType
  description: string | null;
  status: string | null;           // NOT NULL with default 'Open'
  severity: string | null;         // NOT NULL enum
  resolved_at: string | null;      // timestamptz → dateResolved
  reported_by: string | null;      // uuid — store source string in metadata
  assigned_to: string | null;      // uuid — store owner string in metadata
  resolution_notes: string | null;
  metadata: IssueMetadata | null;
  created_at: string;
  updated_at: string;
}

// ── Metadata shape — fields stored in the metadata JSONB column ───────────
interface IssueMetadata {
  issueType?: IssueType;
  photo?: string;
  owner?: string;
  source?: IssueSource;
  relatedContractorId?: string;
  dateInProgress?: string;
  dateClosed?: string;
  // Structured intake fields
  blockerCategory?: IssueBlockerCategory;
  blockerDetail?: string;
  homeownerConcern?: string;
  affectedArea?: IssueAffectedArea;
  suggestedAction?: string;
  jobStageAtCreation?: string;
  // Denormalized for display — avoids needing a join with jobs table
  customerName?: string;
  jobNumber?: string;
}

const ISSUE_SELECT_COLUMNS = [
  "id",
  "organization_id",
  "job_id",
  "issue_number",
  "title",
  "description",
  "status",
  "severity",
  "resolved_at",
  "reported_by",
  "assigned_to",
  "resolution_notes",
  "metadata",
  "created_at",
  "updated_at",
].join(", ");

const ISSUE_JOB_ENRICH_SELECT = "id, job_number, metadata, customers(first_name, last_name)";

// ── Enriches issues with customerName and resolves job UUID → job_number ──
// issues.job_id in the DB may be a UUID FK or a job_number string. This function:
//   1. Tries to look up jobs by UUID (primary key)
//   2. Falls back to looking up by job_number if UUID lookup returns nothing
//   3. Populates customerName from metadata.customerName or customer_name column
type JobLookup = { customerName: string; jobNumber: string };
type JobLookupRow = {
  id: string;
  job_number: string | null;
  customers?: { first_name: string | null; last_name: string | null } | null;
  metadata: { customerName?: string } | null;
};

function toJobLookup(j: JobLookupRow): JobLookup {
  const fromCustomers = j.customers
    ? [j.customers.first_name, j.customers.last_name].filter(Boolean).join(" ")
    : "";
  return {
    customerName: j.metadata?.customerName || fromCustomers || "",
    jobNumber: j.job_number ?? "",
  };
}

async function enrichWithCustomerNames(issues: Issue[]): Promise<Issue[]> {
  const jobIds = [...new Set(issues.map((i) => i.jobId).filter(Boolean))];
  if (jobIds.length === 0) return issues;

  // Primary lookup: treat jobIds as UUIDs (the FK in the DB)
  const { data: byUuidRows } = await supabase
    .from("jobs")
    .select(ISSUE_JOB_ENRICH_SELECT)
    .in("id", jobIds);

  const byUuid = new Map<string, JobLookup>(
    ((byUuidRows ?? []) as JobLookupRow[]).map((j) => [j.id, toJobLookup(j)])
  );

  // Secondary lookup: treat jobIds as job_numbers (issued when job_id was stored as job_number)
  const unmatchedIds = jobIds.filter((id) => !byUuid.has(id));
  const byJobNumber = new Map<string, JobLookup>();
  if (unmatchedIds.length > 0) {
    const { data: byNumberRows } = await supabase
      .from("jobs")
      .select(ISSUE_JOB_ENRICH_SELECT)
      .in("job_number", unmatchedIds);
    ((byNumberRows ?? []) as JobLookupRow[]).forEach((j) => {
      if (j.job_number) byJobNumber.set(j.job_number, toJobLookup(j));
    });
  }

  return issues.map((issue) => {
    const match = byUuid.get(issue.jobId) ?? byJobNumber.get(issue.jobId);
    return {
      ...issue,
      jobId: match?.jobNumber || issue.jobId,
      customerName: issue.customerName || match?.customerName || "",
      jobNumber: issue.jobNumber || match?.jobNumber || issue.jobId,
    };
  });
}

// ── Resolve a stored photo path to a 1-hour signed URL ────────────────────
// Issue photos are uploaded to the qa-photos bucket via the QA form flow.
// The path stored in metadata.photo is a raw storage path, not a URL.
const ISSUE_PHOTO_BUCKET = "qa-photos";

async function resolveIssuePhotoUrl(photo: string | undefined | null): Promise<string> {
  if (!photo) return "";
  // Already a full URL — return as-is (legacy public URL or blob)
  if (photo.startsWith("http") || photo.startsWith("blob:")) return photo;

  return createSignedStorageThumbnailUrl(photo, {
    bucket: ISSUE_PHOTO_BUCKET,
    expiresIn: 3600,
  }).catch(() => "");
}

async function resolveIssuePhotos(issues: Issue[]): Promise<Issue[]> {
  return Promise.all(
    issues.map(async (issue) => {
      if (!issue.photo) return issue;
      const resolved = await resolveIssuePhotoUrl(issue.photo);
      return { ...issue, photo: resolved };
    })
  );
}

type IssueListOptions = {
  resolvePhotos?: boolean;
};

// ── Service ───────────────────────────────────────────────────────────────
export const IssueService = {
  async getAll(options: IssueListOptions = {}): Promise<{ data: Issue[] | null; error: unknown }> {
    const { resolvePhotos = true } = options;
    const { data, error } = await supabase
      .from("issues")
      .select(ISSUE_SELECT_COLUMNS)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    const issues = await enrichWithCustomerNames((data as IssueRow[]).map(toUiIssue));
    return { data: resolvePhotos ? await resolveIssuePhotos(issues) : issues, error: null };
  },

  async getById(issueNumber: string): Promise<{ data: Issue | null; error: unknown }> {
    const { data, error } = await supabase
      .from("issues")
      .select(ISSUE_SELECT_COLUMNS)
      .eq("issue_number", issueNumber)
      .single();

    if (error || !data) return { data: null, error };
    const [enriched] = await enrichWithCustomerNames([toUiIssue(data as IssueRow)]);
    const [resolved] = await resolveIssuePhotos([enriched]);
    return { data: resolved, error: null };
  },

  async getByJobNumber(jobNumber: string): Promise<{ data: Issue[] | null; error: unknown }> {
    // job_id in issues is a UUID FK — resolve job_number → UUID first
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .single();

    if (!jobRow) return { data: [], error: null };

    const { data, error } = await supabase
      .from("issues")
      .select(ISSUE_SELECT_COLUMNS)
      .eq("job_id", jobRow.id)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    const issues = await enrichWithCustomerNames((data as IssueRow[]).map(toUiIssue));
    return { data: await resolveIssuePhotos(issues), error: null };
  },

  async getByStatus(status: IssueStatus): Promise<{ data: Issue[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("issues")
      .select(ISSUE_SELECT_COLUMNS)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as IssueRow[]).map(toUiIssue), error: null };
  },

  async create(
    issue: Issue,
    context?: { customerName?: string; jobNumber?: string, amountAtRisk?: number }
  ): Promise<{ data: Issue | null; error: unknown }> {
    // Resolve job UUID and org_id in parallel — no dependency between them.
    const [{ data: jobRow }, organizationId] = await Promise.all([
      supabase
        .from("jobs")
        .select("id, metadata")
        .eq(jobColumnFor(issue.jobId), issue.jobId)
        .single(),
      getCurrentOrganizationId().catch(() => null),
    ]);

    // Generate next sequential issue_number scoped to this org.
    // The caller's issue.id is a temp placeholder — the service owns this value.
    const lastIssueQuery = supabase
      .from("issues")
      .select("issue_number")
      .order("created_at", { ascending: false })
      .limit(1);
    const { data: lastRow } = await (organizationId
      ? lastIssueQuery.eq("organization_id", organizationId)
      : lastIssueQuery
    ).maybeSingle();
    const lastNum = lastRow?.issue_number
      ? parseInt((lastRow.issue_number as string).replace(/\D/g, ""), 10) || 0
      : 0;
    const issueNumber = `ISS-${String(lastNum + 1).padStart(3, "0")}`;

    const row = toDbIssueCreate({ ...issue, id: issueNumber });
    row.job_id = jobRow?.id ?? issue.jobId;
    row.organization_id = organizationId;

    // Inject denormalized display fields into metadata
    row.metadata = {
      ...row.metadata,
      customerName: context?.customerName,
      jobNumber: context?.jobNumber ?? issue.jobId,
    };

    const { data, error } = await supabase
      .from("issues")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[IssueService.create] Supabase error:", JSON.stringify(error));
      return { data: null, error };
    }
    const [enriched] = await enrichWithCustomerNames([toUiIssue(data as IssueRow)]);

    // Fire-and-forget — enqueue in the Executive Command Center action queue.
    // Non-blocking: failure here must never affect issue creation.
    const amountAtRisk = (jobRow as any)?.metadata?.contractValue as number | undefined;
    ActionQueueService.enqueueFromIssue(enriched, amountAtRisk).catch((err) =>
      console.warn("[IssueService.create] enqueueFromIssue failed:", err)
    );

    return { data: enriched, error: null };
  },

  async update(issueNumber: string, updates: Partial<Issue>): Promise<{ data: Issue | null; error: unknown }> {
    const { data: current, error: fetchError } = await supabase
      .from("issues")
      .select(ISSUE_SELECT_COLUMNS)
      .eq("issue_number", issueNumber)
      .single();

    if (fetchError || !current) return { data: null, error: fetchError };

    const patch = toDbIssueUpdate(updates);

    // Merge metadata with existing DB metadata so partial updates don't wipe
    // fields that were not included in the update payload.
    if (patch.metadata) {
      patch.metadata = { ...(current as IssueRow).metadata, ...patch.metadata };
    }

    const { data, error } = await supabase
      .from("issues")
      .update(patch)
      .eq("issue_number", issueNumber)
      .select()
      .single();

    if (error || !data) return { data: null, error };
    return { data: toUiIssue(data as IssueRow), error: null };
  },

  async delete(issueNumber: string): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("issues")
      .delete()
      .eq("issue_number", issueNumber);
    return { error };
  },

  /** Bulk upsert — for seeding mock data */
  async bulkInsert(issues: Issue[]): Promise<{ count: number; error: unknown }> {
    const rows = issues.map(toDbIssueCreate);
    const { data, error } = await supabase
      .from("issues")
      .upsert(rows, { onConflict: "issue_number" })
      .select("id");
    return { count: data?.length ?? 0, error };
  },
};
